import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';
import './Contact.css';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  
  // EmailJS configuration
  const { SERVICE_ID, CONTACT_TEMPLATE_ID, PUBLIC_KEY } = EMAILJS_CONFIG;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // Prepare email template parameters
      const templateParams = {
        to_email: 'info@pejapeaceforum.org',
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        formatted_message: `
New Contact Form Submission

Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

Message:
${formData.message}

Submitted on: ${new Date().toLocaleString()}
        `
      };

      // Send email using EmailJS
      await emailjs.send(
        SERVICE_ID,
        CONTACT_TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );
      
      console.log('Contact form email sent successfully');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact">
      <section className="contact-hero section">
        <div className="container">
          <div className="contact-hero-content text-center">
            <h1>{t('contact.hero.title')}</h1>
            <p className="hero-subtitle">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="contact-main section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2>{t('contact.getInTouch.title')}</h2>
              <p>
                {t('contact.getInTouch.description')}
              </p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">{t('contact.address.label')}</div>
                  <div className="contact-text">
                    <h4>{t('contact.address.label')}</h4>
                    <p>{t('contact.address.value')}</p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">{t('contact.email.label')}</div>
                  <div className="contact-text">
                    <h4>{t('contact.email.label')}</h4>
                    <p>{t('contact.email.value')}</p>
                  </div>
                </div>
              </div>

              <div className="social-media">
                <h4>{t('contact.social.title')}</h4>
                <div className="social-links">
                  <a href="https://www.facebook.com/profile.php?id=61580003159183" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <svg className="social-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a href="https://www.instagram.com/pejapeaceforum/" className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <svg className="social-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.79 0-1.4.61-1.4 1.4s.61 1.4 1.4 1.4 1.4-.61 1.4-1.4-.61-1.4-1.4-1.4z"/>
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/in/peja-peace-forum-369323381/" className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <svg className="social-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a href="https://x.com/PejaPeaceForum" className="social-link" aria-label="X (Twitter)" target="_blank" rel="noopener noreferrer">
                    <svg className="social-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a href="https://www.youtube.com/@PejaPeaceForum" className="social-link" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                    <svg className="social-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>{t('contact.sendMessage.title')}</h2>
              

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">{t('contact.form.name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contact.form.email')} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contact.form.subject')} *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact.form.selectSubject')}</option>
                    <option value="general">{t('contact.form.subjects.general')}</option>
                    <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                    <option value="volunteer">{t('contact.form.subjects.volunteer')}</option>
                    <option value="media">{t('contact.form.subjects.media')}</option>
                    <option value="events">{t('contact.form.subjects.events')}</option>
                    <option value="other">{t('contact.form.subjects.other')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.form.message')} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="success-message" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '5px', color: '#155724' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>✅ {t('contact.form.success')}</h3>
                    <p style={{ margin: '0', fontSize: '16px' }}>{t('contact.form.successDescription')}</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '5px', color: '#721c24' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>❌ {t('contact.form.error')}</h3>
                    <p style={{ margin: '0', fontSize: '16px' }}>{t('contact.form.errorDescription')}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
